import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Drawer,
  Group,
  NumberInput,
  Stack,
  Switch,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconInfoCircle } from '@tabler/icons-react';
import { isAxiosError } from 'axios';
import type { KitchenStationDto } from './types';
import { useCreateKitchenStation, useUpdateKitchenStation } from './stationsApi';

interface Props {
  opened: boolean;
  onClose: () => void;
  branchId: string;
  station: KitchenStationDto | null;
}

interface FormValues {
  code: string;
  nameAr: string;
  nameEn: string;
  isActive: boolean;
  sortOrder: number;
}

export function StationFormDrawer({ opened, onClose, branchId, station }: Props) {
  const { t, i18n } = useTranslation();
  const create = useCreateKitchenStation();
  const update = useUpdateKitchenStation();
  const submitting = create.isPending || update.isPending;
  const editing = station !== null;
  const isDefault = station?.isDefault === true;
  const drawerPosition = i18n.language === 'ar' ? 'left' : 'right';

  const form = useForm<FormValues>({
    initialValues: { code: '', nameAr: '', nameEn: '', isActive: true, sortOrder: 0 },
    validate: {
      code: (v) => (v.trim() ? null : t('common.errors.required')),
      nameAr: (v) => (v.trim() ? null : t('common.errors.required')),
    },
  });

  useEffect(() => {
    if (!opened) return;
    form.setValues({
      code: station?.code ?? '',
      nameAr: station?.nameAr ?? '',
      nameEn: station?.nameEn ?? '',
      isActive: station?.isActive ?? true,
      sortOrder: station?.sortOrder ?? 0,
    });
    form.resetDirty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, station]);

  const submit = async (values: FormValues) => {
    const payload = {
      code: values.code.trim(),
      nameAr: values.nameAr.trim(),
      nameEn: values.nameEn.trim() || null,
      sortOrder: values.sortOrder,
    };
    try {
      if (editing) {
        await update.mutateAsync({
          id: station.kitchenStationId,
          request: { ...payload, isActive: values.isActive },
        });
      } else {
        await create.mutateAsync({ branchId, ...payload });
      }
      notifications.show({
        color: 'green',
        title: editing ? t('common.toasts.updated') : t('common.toasts.created'),
        message: payload.nameAr,
      });
      onClose();
    } catch (err) {
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: extractApiMessage(err) ?? t('common.toasts.errorBody'),
      });
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={editing ? t('kitchenStations.form.editTitle') : t('kitchenStations.form.createTitle')}
      position={drawerPosition}
      size="md"
      padding="lg"
    >
      <form onSubmit={form.onSubmit(submit)}>
        <Stack gap="md">
          {isDefault && (
            <Alert color="blue" variant="light" icon={<IconInfoCircle size={16} stroke={1.6} />}>
              {t('kitchenStations.form.defaultNotice')}
            </Alert>
          )}
          <TextInput
            label={t('kitchenStations.form.codeLabel')}
            description={t('kitchenStations.form.codeHelp')}
            placeholder="GRILL"
            withAsterisk
            {...form.getInputProps('code')}
          />
          <TextInput
            label={t('kitchenStations.form.nameArLabel')}
            placeholder={t('kitchenStations.form.nameArPlaceholder')}
            withAsterisk
            {...form.getInputProps('nameAr')}
          />
          <TextInput
            label={t('kitchenStations.form.nameEnLabel')}
            placeholder={t('kitchenStations.form.nameEnPlaceholder')}
            {...form.getInputProps('nameEn')}
          />
          <NumberInput
            label={t('kitchenStations.form.sortOrderLabel')}
            description={t('kitchenStations.form.sortOrderHelp')}
            min={0}
            {...form.getInputProps('sortOrder')}
          />
          {editing && (
            <Switch
              label={t('kitchenStations.form.activeLabel')}
              description={t('kitchenStations.form.activeHelp')}
              checked={form.values.isActive}
              onChange={(event) => form.setFieldValue('isActive', event.currentTarget.checked)}
              disabled={isDefault}
            />
          )}

          <Group justify="flex-end" gap="sm" mt="sm">
            <Button variant="default" onClick={onClose} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={submitting}>
              {editing ? t('common.save') : t('common.create')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  );
}

function extractApiMessage(err: unknown): string | null {
  if (isAxiosError(err)) {
    const data = err.response?.data as { detail?: string; title?: string } | undefined;
    return data?.detail ?? data?.title ?? err.message;
  }
  return null;
}
